import * as React from "react";
import { useDialog } from "@shared-ui/dialog-context";
import { LinksDialogContext } from "components/flexComponents/Links/GridView/LinkDialogContext";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import Links from "components/flexComponents/Links/Links";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkIcon } from "uitk-react-icons";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkButton } from "uitk-react-button";
import { UitkLink } from "uitk-react-link";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { DialogProps, LinksGridItemProps, LinksTemplateComponent } from "../typings";
import { ExperienceTemplateRenderer } from "experience-template-renderer-react";

export const LinksGridItem = withStores("flexModuleModelStore")(
  observer((props: LinksGridItemProps) => {
    const { view, group, id, flexModuleModelStore, context, focusLinkGridRef, templateComponent } = props;
    const [, dialogActions] = useDialog(`linksDialog-${id}`);

    const linkRef = React.createRef<HTMLButtonElement | HTMLAnchorElement>();

    let linksGridItem = null;
    if (templateComponent) {
      linksGridItem = templateComponent;
    }
    if (group) {
      linksGridItem = group.templateComponent;
    }
    if (!linksGridItem) {
      return null;
    }

    const { children, metadata } = linksGridItem;
    if (!metadata?.id || !children) {
      return null;
    }
    const model = flexModuleModelStore.getModel(metadata.id);
    if (!model) {
      return null;
    }
    const linksDialogContext = LinksDialogContext;

    // @ts-ignore
    const { title, subheading, dialogHeading, subHeadingAlignment } = model;
    const linkModules = children.filter(
      (component) => component.type === "links" || component.type === "link-juice"
    ) as LinksTemplateComponent[];
    const openDialog = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
      linksDialogContext.dialogHeading = dialogHeading;
      linksDialogContext.modules = linkModules;
      focusLinkGridRef.current = linkRef.current;
      dialogActions.openDialog();
    };
    const keyHelper = new ItemKeyHelper(`LinksGridItem-${id}`);
    const links = linkModules?.map((module) => {
      return (
        <div aria-hidden="true" hidden key={keyHelper.next()}>
          {group && module.type === "link-juice" ? (
            <BlossomComponents context={context} blossomComponent={group} />
          ) : templateComponent && module.type === "link-juice" ? (
            <ExperienceTemplateRenderer templateComponents={linkModules} />
          ) : (
            <Links templateComponent={module} key={keyHelper.next()} />
          )}
        </div>
      );
    });

    const dialogProps: DialogProps = { title, subheading, subHeadingAlignment, openDialog, linkRef };

    let dialogView;
    if (view === "row-trigger") {
      dialogView = <DialogViewWithIcon {...dialogProps} />;
    } else if (view === "standard-link") {
      dialogView = <DialogViewLinkableText {...dialogProps} />;
    }

    return (
      <div className="LinksGridItem" key={keyHelper.next()}>
        <UitkCard className={view}>{dialogView}</UitkCard>
        <div className="is-visually-hidden">{links}</div>
      </div>
    );
  })
);

const DialogViewWithIcon: React.FC<DialogProps> = observer(({ title, subheading, openDialog, linkRef }) => {
  return (
    <UitkCardContentSection>
      <UitkButton onClick={openDialog} isFullWidth aria-label={title} domRef={linkRef}>
        <UitkLayoutFlex justifyContent="space-between" alignItems="center" className="LinksGridItem">
          <UitkLayoutFlexItem>
            <div className="uitk-type-left">
              <UitkHeading tag="h3" size={7}>
                {title}
              </UitkHeading>
              {subheading && <UitkSubheading>{subheading}</UitkSubheading>}
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkIcon name="chevron_right" />
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkButton>
    </UitkCardContentSection>
  );
});

const DialogViewLinkableText: React.FC<DialogProps> = observer(
  ({ title, subheading, subHeadingAlignment, openDialog, linkRef }) => {
    return (
      <UitkCardContentSection>
        <UitkLayoutFlexItem>
          <UitkLayoutFlex justifyContent={subHeadingAlignment === "center" ? "center" : "start"}>
            <UitkLayoutFlexItem className={subHeadingAlignment === "center" ? "links-item-center" : ""}>
              <div>
                <UitkHeading tag="h3" size={7}>
                  {title}
                </UitkHeading>
                <UitkLink onClick={openDialog} align={subHeadingAlignment === "center" ? "center" : "left"}>
                  <a href="" role="button" ref={linkRef}>
                    {subheading}
                  </a>
                </UitkLink>
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
      </UitkCardContentSection>
    );
  }
);

export default LinksGridItem;
